// HERE Flexible Polyline Decoder
// Simplified implementation based on HERE Flexible Polyline specification

const FORMAT_VERSION = 1
const DECODING_TABLE = [
  62, -1, -1, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1, -1, -1, -1, -1,
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
  22, 23, 24, 25, -1, -1, -1, -1, 63, -1, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35,
  36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51
]

function decodeChar (charValue) {
  const pos = charValue.charCodeAt(0) - 45
  if (pos < 0 || pos > 77) {
    return -1
  }
  return DECODING_TABLE[pos]
}

class Converter {
  constructor (precision) {
    this.multiplier = Math.pow(10, precision)
    this.lastValue = 0
  }

  decodeUnsignedVarint (encodedChars, indexRef) {
    let shift = 0
    let delta = 0
    let value = 0

    while (indexRef.index < encodedChars.length) {
      value = decodeChar(encodedChars[indexRef.index])
      if (value < 0) {
        return null // Invalid encoding
      }
      indexRef.index++
      delta |= (value & 0x1F) << shift
      if ((value & 0x20) === 0) {
        return delta
      } else {
        shift += 5
      }
    }
    if (shift > 0) {
      return null // Incomplete encoding
    }
    return delta
  }

  decodeValue (encodedChars, indexRef) {
    let delta = this.decodeUnsignedVarint(encodedChars, indexRef)
    if (delta === null) {
      return null
    }

    if ((delta & 1) !== 0) {
      delta = ~delta
    }
    delta >>= 1
    this.lastValue += delta
    return this.lastValue / this.multiplier
  }
}

class PolylineDecoder {
  constructor (encoded) {
    this.encoded = encoded.split('')
    this.index = 0
    this.precision = 0
    this.thirdDimPrecision = 0
    this.thirdDimension = 0 // 0 for Absent

    this.decodeHeader()

    this.latConverter = new Converter(this.precision)
    this.lngConverter = new Converter(this.precision)
    this.zConverter = new Converter(this.thirdDimPrecision)
  }

  decodeHeader () {
    const indexRef = { index: this.index }
    let value = this.decodeUnsignedVarint(this.encoded, indexRef)
    if (value === null || value !== FORMAT_VERSION) {
      throw new Error('Invalid format version')
    }

    value = this.decodeUnsignedVarint(this.encoded, indexRef)
    if (value === null) {
      throw new Error('Invalid encoding for header')
    }

    this.precision = value & 0b1111
    value >>= 4
    this.thirdDimension = value & 0b111
    this.thirdDimPrecision = (value >> 3) & 0b1111
    this.index = indexRef.index
  }

  decodeOne () {
    if (this.index >= this.encoded.length) {
      return null
    }

    const indexRef = { index: this.index }
    const lat = this.latConverter.decodeValue(this.encoded, indexRef)
    const lng = this.lngConverter.decodeValue(this.encoded, indexRef)
    let z = 0

    if (this.thirdDimension !== 0) { // ThirdDimension.Absent
      z = this.zConverter.decodeValue(this.encoded, indexRef)
    }

    if (lat === null || lng === null || (this.thirdDimension !== 0 && z === null)) {
      throw new Error('Invalid encoding for coordinates')
    }

    this.index = indexRef.index
    return { lat, lng, z }
  }

  decode () {
    const result = []
    let point = this.decodeOne()
    while (point !== null) {
      result.push(point)
      point = this.decodeOne()
    }
    return result
  }

  decodeUnsignedVarint (encodedChars, indexRef) {
    let shift = 0
    let delta = 0
    let value = 0

    while (indexRef.index < encodedChars.length) {
      value = decodeChar(encodedChars[indexRef.index])
      if (value < 0) {
        return null // Invalid encoding
      }
      indexRef.index++
      delta |= (value & 0x1F) << shift
      if ((value & 0x20) === 0) {
        return delta
      } else {
        shift += 5
      }
    }
    if (shift > 0) {
      return null // Incomplete encoding
    }
    return delta
  }
}

/**
 * Dekoduje HERE Flexible Polyline do listy współrzędnych
 * @param {string} encoded - Zakodowany polyline string
 * @returns {Array} Lista punktów {lat, lng}
 */
export function decodeHerePolyline (encoded) {
  if (!encoded || typeof encoded !== 'string') {
    console.warn('Invalid polyline input:', encoded)
    return []
  }

  try {
    const decoder = new PolylineDecoder(encoded)
    const decoded = decoder.decode()
    const result = decoded.map(p => ({ lat: p.lat, lng: p.lng }))
    return result
  } catch (error) {
    console.error('Error decoding HERE polyline:', error)
    return []
  }
}

/**
 * Ekstraktuje dane trasy z HERE API response
 * @param {string} apiResponseString - Response z HERE API jako string
 * @returns {Object} Obiekt z decoded polyline i innymi danymi trasy
 */
export function extractRouteFromHereResponse (apiResponseString) {
  if (!apiResponseString) {
    return { coordinates: [], routeInfo: null }
  }

  try {
    const response = JSON.parse(apiResponseString)

    const firstRoute = response.routes?.[0]
    const sections = firstRoute?.sections

    if (!sections || sections.length === 0) {
      console.warn('No sections found in HERE API response.')
      return { coordinates: [], routeInfo: null }
    }

    let allCoordinates = []
    const combinedRouteInfo = {
      duration: 0,
      length: 0,
      tolls: [],
      notices: []
    }

    // Przetwórz wszystkie sekcje trasy
    sections.forEach((section, index) => {
      if (section.polyline) {
        const sectionCoordinates = decodeHerePolyline(section.polyline)

        // Dodaj współrzędne z sekcji, unikając duplikatów na połączeniach sekcji
        if (allCoordinates.length > 0 && sectionCoordinates.length > 0) {
          // Sprawdź czy ostatni punkt poprzedniej sekcji jest taki sam jak pierwszy punkt tej sekcji
          const lastPoint = allCoordinates[allCoordinates.length - 1]
          const firstPoint = sectionCoordinates[0]

          // Jeśli punkty są bardzo blisko siebie (różnica mniejsza niż 0.00001 stopnia), pomiń pierwszy punkt sekcji
          if (Math.abs(lastPoint.lat - firstPoint.lat) < 0.00001 &&
              Math.abs(lastPoint.lng - firstPoint.lng) < 0.00001) {
            allCoordinates = allCoordinates.concat(sectionCoordinates.slice(1))
          } else {
            allCoordinates = allCoordinates.concat(sectionCoordinates)
          }
        } else {
          allCoordinates = allCoordinates.concat(sectionCoordinates)
        }
      }

      // Agreguj dane podsumowania z każdej sekcji
      if (section.summary) {
        if (section.summary.duration) {
          combinedRouteInfo.duration += section.summary.duration
        }
        if (section.summary.length) {
          combinedRouteInfo.length += section.summary.length
        }
      }

      // Agreguj opłaty i powiadomienia z każdej sekcji
      if (section.tolls) {
        combinedRouteInfo.tolls = combinedRouteInfo.tolls.concat(section.tolls)
      }
      if (section.notices) {
        combinedRouteInfo.notices = combinedRouteInfo.notices.concat(section.notices)
      }
    })

    if (allCoordinates.length === 0) {
      console.warn('No valid polylines found in any section of HERE API response.')
      return { coordinates: [], routeInfo: null }
    }

    return { coordinates: allCoordinates, routeInfo: combinedRouteInfo }
  } catch (error) {
    console.error('Error parsing HERE API response or decoding polyline:', error)
    return { coordinates: [], routeInfo: null }
  }
}
